import { useSubmit } from "@certn/api";
import { Stack, Text, showToast } from "@certn/cascadia";
import {
  Container,
  Divider,
  MediaQuery,
  mq,
  Text as StepHeadingText,
} from "@certn/cascadia-react";
import {
  EVENT_NAMES,
  RegionContext,
  RegionProvider,
  useEventTracking,
} from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useOrderBillingProfile } from "./useOrderBillingProfile";
import { useOrderBlockingProfile } from "./useOrderBlockingProfile";
import { OrderReviewEmailPreview } from "../../../components/ordering-review/OrderReviewEmailPreview";
import { OrderReviewSummaryBox } from "../../../components/ordering-review/OrderReviewSummaryBox";
import { WwccLimitationsMessage } from "../../../components/ordering-review/WwccLimitationsMessage";
import { ConsumerReportCertificationSection } from "../../../components/ordering-review/ConsumerReportCertificationSection";
import { BillingAddress } from "../../../components/ordering-review/billing/BillingAddress";
import { PaymentProfile } from "../../../components/ordering-review/payment-profile/PaymentProfile";
import { useUser } from "../../../hooks/useUser";
import { OrderContext } from "../OrderContext";
import { clearOrderDraft, useOrderDraftKey } from "../orderDraftStorage";
import { OrderBlockedMessage } from "../components/OrderBlockedMessage";
import { OrderStepLayout } from "../components/OrderStepLayout";
import {
  PlaceOrderDisabledReasons,
  getPlaceOrderDisabledReasons,
} from "../components/PlaceOrderDisabledReasons";
import {
  enabledNotificationChannels,
  orderContextToSubmitOrderPayload,
  orderIncludesAuWwcc,
  orderRequiresEmploymentConsumerReportCertification,
  showSubmitOrderError,
} from "../order-utils";
import { useCanOrderCVReview } from "../../../components/cv-review/utils";
import { CVReviewExtraction } from "../../../components/cv-review/CVReviewExtraction";
import { useFocusStepHeading } from "../useFocusStepHeading";

export function ReviewStepDailyPay() {
  const { order, goToNextStep } = useContext(OrderContext);
  const { t } = useTranslation();
  const stepHeadingId = useFocusStepHeading();
  const { applicantSmsMessaging, applicantWhatsappMessaging } = useFlags();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();
  const { trackEvent } = useEventTracking();
  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/submit-order/`,
    {
      throwErrors: true,
    }
  );
  const { data: profile } = useOrderBillingProfile();
  const { isBlocked, blockingProfile } = useOrderBlockingProfile();
  const [
    consumerReportCertificationAcknowledged,
    setConsumerReportCertificationAcknowledged,
  ] = useState(false);
  const [disableCompletion, setDisableCompletion] = useState<boolean>(false);
  const certificationRequired =
    orderRequiresEmploymentConsumerReportCertification(order, userDetails);
  const disabledReasons = getPlaceOrderDisabledReasons({
    hasNoRecipients: order.recipients.length === 0,
    isMissingPaymentMethod: !!profile && !profile.payment_profile,
    isMissingBillingAddress:
      !!profile &&
      (!profile.address.address_line_1 ||
        !profile.address.locality ||
        !profile.address.postal_code),
    isOrderBlocked: isBlocked,
    isCertificationUnacknowledged:
      certificationRequired && !consumerReportCertificationAcknowledged,
    isCvReviewInvalid: disableCompletion,
  });
  const disablePlaceOrder = disabledReasons.length > 0;

  const { canOrderCVReview, templateId, verificationType } =
    useCanOrderCVReview({
      regularChecks: order.checks,
      packageChecks: order.packageData?.checks || [],
    });

  const submitOrder = async () => {
    try {
      await submit(
        orderContextToSubmitOrderPayload(order, userDetails, {
          consumerReportCertificationAcknowledged:
            certificationRequired && consumerReportCertificationAcknowledged,
          enabledNotificationChannels: enabledNotificationChannels({
            applicantSmsMessaging,
            applicantWhatsappMessaging,
          }),
        })
      );
      trackEvent(EVENT_NAMES.CP_SUBMITTED_PAYMENT, {
        paymentMethod: (profile?.payment_method ?? "credit_card").toLowerCase(),
        checksOrdered: order.checks.map((c) => c.key).join(","),
      });
      clearOrderDraft(orderDraftKey);
      showToast(t("Order submitted"), { type: "info" });
      goToNextStep();
    } catch (e) {
      showSubmitOrderError(e, t);
    }
  };

  return (
    <OrderStepLayout
      onComplete={submitOrder}
      disableCompletion={disablePlaceOrder}
      submitting={isSubmitting}
      scrollRegionAriaLabel={t("Order review content")}
    >
      <Container layout="wide">
        <StepHeadingText
          id={stepHeadingId}
          as="h1"
          tabIndex={-1}
          style="global/title-small"
        >
          {t("Review & pay")}
        </StepHeadingText>

        {isBlocked && blockingProfile && (
          <OrderBlockedMessage profile={blockingProfile} />
        )}

        <div className={styles.page}>
          <Stack className={styles.summary} gap="space-4">
            <PlaceOrderDisabledReasons reasons={disabledReasons} />
            <div className={styles.orderSummary}>
              <RegionProvider>
                <RegionContext.Consumer>
                  {(region) => (
                    <OrderReviewSummaryBox
                      order={order}
                      onPlaceOrder={submitOrder}
                      isSubmitting={isSubmitting}
                      disablePlaceOrder={disablePlaceOrder}
                      region={region}
                    />
                  )}
                </RegionContext.Consumer>
              </RegionProvider>
            </div>
            {profile?.invoicing_frequency === "DAILY" && (
              <Text style="global/body-secondary" color="text-weak">
                {t(
                  "Payments will be charged when the case is complete. Your card will be pre-authorized for this order amount."
                )}
              </Text>
            )}
            {orderIncludesAuWwcc(order) && <WwccLimitationsMessage />}
            {certificationRequired && (
              <ConsumerReportCertificationSection
                acknowledged={consumerReportCertificationAcknowledged}
                onAcknowledgedChange={
                  setConsumerReportCertificationAcknowledged
                }
              />
            )}
            {profile?.payment_method === "CREDIT_CARD" && (
              <div className={styles.billing}>
                <BillingAddress />
                <MediaQuery sm md>
                  <Divider />
                </MediaQuery>
                <PaymentProfile />
              </div>
            )}
            {canOrderCVReview && order.cv_review_id && (
              <CVReviewExtraction
                cv_review_id={order.cv_review_id}
                templateId={templateId}
                verificationType={verificationType}
                setDisableCompletion={setDisableCompletion}
              />
            )}
            <OrderReviewEmailPreview order={order} />
          </Stack>
          <div className={styles.orderSummaryWide}>
            <RegionProvider>
              <RegionContext.Consumer>
                {(region) => (
                  <OrderReviewSummaryBox
                    order={order}
                    onPlaceOrder={submitOrder}
                    isSubmitting={isSubmitting}
                    disablePlaceOrder={disablePlaceOrder}
                    region={region}
                    border="dropshadow"
                  />
                )}
              </RegionContext.Consumer>
            </RegionProvider>
          </div>
        </div>
      </Container>
    </OrderStepLayout>
  );
}

const styles = {
  page: css({
    display: "flex",
    gap: "var(--space-6)",
    flexWrap: "wrap-reverse",
  }),
  summary: css({ flex: "1 1 540px", minWidth: 0 }),
  billing: css(
    { display: "flex", gap: "var(--space-4)" },
    mq({
      flexDirection: ["column-reverse", "column-reverse", "column"],
    })
  ),
  orderSummary: css(
    { flexBasis: 304 },
    mq({ display: ["block", "block", "none"] })
  ),
  orderSummaryWide: css(
    { flex: "1 1 304px" },
    mq({ display: ["none", "none", "block"] })
  ),
};
